import * as XLSX from "xlsx";
import {saveAs} from "file-saver";

export const exportAlertsToExcel = (alerts: any[], fileName = "alerts-"+new Date().toLocaleDateString("tr-TR")+ ".xlsx") => {
    const data = alerts.map(alert => ({
        "Alert ID": alert.id,
        "Store": alert.store?.name ?? "",
        "Receipt No": alert.receiptNumber,
        "Scenario": alert.scenarioName,
        "Rule": alert.ruleName,
        "Status": alert.status,
        "Reviewed By": alert.reviewedBy ?? "",
        "Date": alert.date,
        "Cashier": alert.cashier ?? "",
        "Amount": alert.alertedAmount ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Alerts");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
};

export const printAlerts = (alerts: any[]) => {
    const html = alerts.map(alert => `
        <tr>
            <td>${alert.id}</td>
            <td>${alert.store?.name ?? ""}</td>
            <td>${alert.receiptNumber}</td>
            <td>${alert.scenarioName}</td>
            <td>${alert.ruleName}</td>
            <td>${alert.status}</td>
            <td>${alert.reviewedBy ?? ""}</td>
            <td>${alert.date}</td>
        </tr>
    `).join('');

    const newWin = window.open("");
    newWin!.document.write(`
      <html lang="tr">
        <head>
          <title>Alerts</title>
          <style>
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          </style>
        </head>
        <body>
          <h3>Alert Report</h3>
          <table>
            <thead>
              <tr>
                <th>Alert ID</th>
                <th>Store</th>
                <th>Receipt No</th>
                <th>Scenario</th>
                <th>Rule</th>
                <th>Status</th>
                <th>Reviewed By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${html}
            </tbody>
          </table>
        </body>
      </html>
    `);
    newWin!.document.close();
    newWin!.print();
};

export const exportTransactionsToExcel = (transactions: any[], fileName?: string) => {
    if (!fileName) {
        const date = new Date().toLocaleDateString("tr-TR");
        fileName = `transactions-${date}.xlsx`;
    }
    
    const data = transactions.map(transaction => ({
        "Transaction ID": transaction.transactionId || transaction.id || "",
        "Receipt Number": transaction.receiptNumber || "",
        "Store": transaction.storeData?.name || transaction.store?.name || "",
        "Store City": transaction.storeData?.city || transaction.store?.city || "",
        "Store Province": transaction.storeData?.province || transaction.store?.province || "",
        "Date": transaction.date || transaction.businessDate || "",
        "Time": transaction.time || "",
        "Cashier ID": transaction.cashier || "",
        "Checkout": transaction.checkout || "",
        "Transaction Type": transaction.transaction_type || transaction.transactionType || "",
        "Tender Type": transaction.tender_type || transaction.paymentType || "",
        "Total Amount": transaction.total || transaction.totalGrossAmount || 0,
        "Tax Amount": transaction.taxAmount || transaction.totalTaxAmount || 0,
        "Payment Amount": transaction.paymentAmount || 0,
        "Credit Card": transaction.credit_card || "",
        "Currency": transaction.currency || "",
        "Alert Count": transaction.alert_count || (transaction.alerts?.length || 0),
        "Canceled": transaction.canceled ? "Yes" : "No",
        "Voided": transaction.voided ? "Yes" : "No",
        "Returned": transaction.returned ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    // Set column widths for better readability
    const columnWidths = [
        { wch: 15 }, // Transaction ID
        { wch: 15 }, // Receipt Number
        { wch: 20 }, // Store
        { wch: 15 }, // Store City
        { wch: 15 }, // Store Province
        { wch: 18 }, // Date
        { wch: 10 }, // Time
        { wch: 12 }, // Cashier ID
        { wch: 10 }, // Checkout
        { wch: 18 }, // Transaction Type
        { wch: 15 }, // Tender Type
        { wch: 15 }, // Total Amount
        { wch: 12 }, // Tax Amount
        { wch: 15 }, // Payment Amount
        { wch: 12 }, // Credit Card
        { wch: 10 }, // Currency
        { wch: 12 }, // Alert Count
        { wch: 10 }, // Canceled
        { wch: 10 }, // Voided
        { wch: 10 }, // Returned
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
};

export const exportTransactionsToCSV = (transactions: any[], fileName?: string) => {
    if (!fileName) {
        const date = new Date().toLocaleDateString("tr-TR");
        fileName = `transactions-${date}.csv`;
    }
    
    const data = transactions.map(transaction => ({
        "Transaction ID": transaction.transactionId || transaction.id || "",
        "Receipt Number": transaction.receiptNumber || "",
        "Store": transaction.storeData?.name || transaction.store?.name || "",
        "Store City": transaction.storeData?.city || transaction.store?.city || "",
        "Store Province": transaction.storeData?.province || transaction.store?.province || "",
        "Date": transaction.date || transaction.businessDate || "",
        "Time": transaction.time || "",
        "Cashier ID": transaction.cashier || "",
        "Checkout": transaction.checkout || "",
        "Transaction Type": transaction.transaction_type || transaction.transactionType || "",
        "Tender Type": transaction.tender_type || transaction.paymentType || "",
        "Total Amount": transaction.total || transaction.totalGrossAmount || 0,
        "Tax Amount": transaction.taxAmount || transaction.totalTaxAmount || 0,
        "Payment Amount": transaction.paymentAmount || 0,
        "Credit Card": transaction.credit_card || "",
        "Currency": transaction.currency || "",
        "Alert Count": transaction.alert_count || (transaction.alerts?.length || 0),
        "Canceled": transaction.canceled ? "Yes" : "No",
        "Voided": transaction.voided ? "Yes" : "No",
        "Returned": transaction.returned ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, fileName);
};

export const printTransactions = (transactions: any[]) => {
    const html = transactions.map(transaction => `
        <tr>
            <td>${transaction.receiptNumber || ""}</td>
            <td>${transaction.storeData?.name || transaction.store?.name || ""}</td>
            <td>${transaction.date || ""}</td>
            <td>${transaction.cashier || ""}</td>
            <td>${transaction.transaction_type || transaction.transactionType || ""}</td>
            <td>${transaction.tender_type || transaction.paymentType || ""}</td>
            <td>${transaction.total || transaction.totalGrossAmount || 0}</td>
            <td>${transaction.alert_count || (transaction.alerts?.length || 0)}</td>
        </tr>
    `).join('');

    const newWin = window.open("");
    newWin!.document.write(`
      <html lang="tr">
        <head>
          <title>Transactions</title>
          <style>
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f0f0f0; font-weight: bold; }
          </style>
        </head>
        <body>
          <h3>Transaction Report</h3>
          <p>Total Transactions: ${transactions.length}</p>
          <table>
            <thead>
              <tr>
                <th>Receipt No</th>
                <th>Store</th>
                <th>Date</th>
                <th>Cashier</th>
                <th>Transaction Type</th>
                <th>Payment Type</th>
                <th>Total Amount</th>
                <th>Alerts</th>
              </tr>
            </thead>
            <tbody>
              ${html}
            </tbody>
          </table>
        </body>
      </html>
    `);
    newWin!.document.close();
    newWin!.print();
};

